import type { ResultOf } from '@graphql-typed-document-node/core'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithQuery } from 'jotai-tanstack-query'
import { RestaurantInventoryCourse } from '~/components/course/RestaurantInventoryCourse.frag'
import type {
  ChildUse,
  FilterCriteria,
  FreeFlows,
  SeatAttributes as SeatAttributesType,
  SmokingAllowed,
} from '~/components/filterChips'
import {
  PlanAttributes,
  SeatAttributesFragment,
  createFilterCriteria,
  filterPlanByCriteria,
  filterSeatByCriteria,
  filterSeatBySeatType,
  hasAfternoonTea,
  hasBuffet,
  hasChildUses,
  hasFreeFlow,
  hasSeatAttributes,
  hasSmokingAllowed,
  showFilterChips,
  useBaseSetAfternoonTea,
  useBaseSetFreeFlows,
} from '~/components/filterChips'
import { filterPlanByPriceRange, hasChristmas } from '~/components/filterChips/criteria'
import { useBaseSetChristmas } from '~/components/filterChips/hooks'
import { PlanSelectionInfo } from '~/components/plan/PlanSelectionInfo.frag'
import type { PriceCriteria } from '~/features/search/criteria'
import { getFragmentData, graphql } from '~/lib/graphql'
import type { CourseSelectionInventoryCoursesQueryVariables } from '~/lib/graphql/graphql'
import type { DateText, HourMinute, RestaurantId } from '~/lib/graphql/scalars'
import { createScope } from '~/lib/jotai'
import type { CourseSelectContext } from '~/navigation'
import { createQueryAtom, createQueryOptions } from '~/stores/jotaiQueryHelper'

export const CourseSelectionInventoryCourses = graphql(/* GraphQL */ `
  query CourseSelectionInventoryCourses(
    $restaurantId: RestaurantId!
    $visitorsCount: Int!
    $visitDate: Date!
    $visitTime: HourMinute!
  ) {
    restaurant(restaurantId: $restaurantId) {
      restaurantId
      name
      ...PlanRestaurantAllowDiscountOnReservation
      inventory(visitDate: $visitDate, visitorsCount: $visitorsCount, visitTime: $visitTime) {
        seatOnlyPlans {
          plan {
            ...PlanSelectionInfo
            ...PlanAttributes
          }
          seats {
            ...SeatAttributes
          }
          fixedPoints
        }
        courses {
          ...RestaurantInventoryCourse
        }
      }
    }
  }
`)

const DEBUG_PREFIX = 'CourseSelection'

export const scope = createScope(DEBUG_PREFIX)

const context$ = scope.atom<CourseSelectContext | undefined>(undefined, 'context')

export function useSetContext() {
  return useSetAtom(context$)
}

const variables$ = atom<CourseSelectionInventoryCoursesQueryVariables>((get) => {
  const context = get(context$)
  if (context === undefined) {
    return {
      restaurantId: '' as RestaurantId,
      visitorsCount: 0,
      visitDate: '' as DateText,
      visitTime: '' as HourMinute,
    }
  }
  return {
    restaurantId: context.restaurantId,
    visitorsCount: context.visitorsCount,
    visitDate: context.visitDate,
    visitTime: context.visitTime,
  }
})
variables$.debugLabel = `${DEBUG_PREFIX}-variables$`

const query$ = createQueryAtom<typeof CourseSelectionInventoryCourses>()
query$.debugLabel = `${DEBUG_PREFIX}-query$`

export const coursesQuery$ = atomWithQuery((get) =>
  createQueryOptions(CourseSelectionInventoryCourses, get(variables$), get(query$).fn, {
    enabled: get(variables$).restaurantId !== '',
    // 最新の在庫情報を取得するため、キャッシュしない
    gcTime: 0,
    staleTime: 0,
  })
)
coursesQuery$.debugLabel = `${DEBUG_PREFIX}-coursesQuery$`

export function useQuery() {
  return useAtomValue(coursesQuery$)
}

const filterCriteria$ = atom<FilterCriteria | undefined>((get) => {
  const ctx = get(context$)
  if (ctx == undefined) {
    return undefined
  }
  return createFilterCriteria(ctx)
})
filterCriteria$.debugLabel = `${DEBUG_PREFIX}-filterCriteria$`

export function useFilterCriteria() {
  return useAtomValue(filterCriteria$)
}

// 取得したコースリストからプランをフラットに取り出すための atom
// 絞り込み条件を抽出するために使用するのみで、表示用には使わない
export const allPlans$ = atom<ResultOf<typeof PlanAttributes>[]>((get) => {
  return (
    get(coursesQuery$)
      .data?.restaurant?.inventory.courses.flatMap(
        (c) => getFragmentData(RestaurantInventoryCourse, c).plans
      )
      .map((plan) => getFragmentData(PlanAttributes, plan.plan)) ?? []
  )
})
allPlans$.debugLabel = `${DEBUG_PREFIX}-allPlans$`

// 取得したコースリストから席をフラットに取り出すための atom
// 絞り込み条件を抽出するために使用するのみで、表示用には使わない
// 席のみプランも絞り込み条件に寄与する
const allSeats$ = atom<ResultOf<typeof SeatAttributesFragment>[]>((get) => {
  const inventory = get(coursesQuery$).data?.restaurant?.inventory
  const seatOnlyPlansSeats =
    inventory?.seatOnlyPlans.flatMap((p) => getFragmentData(SeatAttributesFragment, p.seats)) ?? []
  const coursesSeats =
    inventory?.courses.flatMap((c) =>
      getFragmentData(RestaurantInventoryCourse, c).plans.flatMap((p) =>
        getFragmentData(SeatAttributesFragment, p.seats)
      )
    ) ?? []
  // コース在庫がない場合は席の FilterChip を表示しない
  return coursesSeats.length == 0 ? [] : seatOnlyPlansSeats.concat(coursesSeats)
})
allSeats$.debugLabel = `${DEBUG_PREFIX}-allSeats$`

const freeFlows$ = atom<FreeFlows>((get) => {
  return hasFreeFlow(get(allPlans$), get(filterCriteria$))
})
freeFlows$.debugLabel = `${DEBUG_PREFIX}-freeFlows$`

export function useFreeFlows() {
  return useAtomValue(freeFlows$)
}

const afternoonTea$ = atom<boolean>((get) => {
  return hasAfternoonTea(get(allPlans$), get(filterCriteria$))
})
afternoonTea$.debugLabel = `${DEBUG_PREFIX}-afternoonTea$`

export function useAfternoonTea() {
  return useAtomValue(afternoonTea$)
}

const christmas$ = atom<boolean>((get) => {
  return hasChristmas(get(allPlans$), get(filterCriteria$))
})
christmas$.debugLabel = `${DEBUG_PREFIX}-christmas$`

export function useChristmas() {
  return useAtomValue(christmas$)
}

const buffet$ = atom<boolean>((get) => {
  return hasBuffet(get(allPlans$), get(filterCriteria$))
})
buffet$.debugLabel = `${DEBUG_PREFIX}-buffet$`

export function useBuffet() {
  return useAtomValue(buffet$)
}

const smokingAllowed$ = atom<SmokingAllowed>((get) => {
  return hasSmokingAllowed(get(allSeats$), get(filterCriteria$))
})
smokingAllowed$.debugLabel = `${DEBUG_PREFIX}-smokingAllowed$`

export function useSmokingAllowed() {
  return useAtomValue(smokingAllowed$)
}

const seatAttributes$ = atom<SeatAttributesType>((get) => {
  return hasSeatAttributes(get(allSeats$), get(filterCriteria$))
})
seatAttributes$.debugLabel = `${DEBUG_PREFIX}-seatAttributes$`

export function useSeatAttributes() {
  return useAtomValue(seatAttributes$)
}

const childUses$ = atom<ChildUse>((get) => {
  return hasChildUses(get(allSeats$), get(filterCriteria$))
})
childUses$.debugLabel = `${DEBUG_PREFIX}-childUses$`

export function useChildUse() {
  return useAtomValue(childUses$)
}

const show$ = atom((get) => {
  const buffet = get(buffet$)
  const freeFlows = get(freeFlows$)
  const afternoonTea = get(afternoonTea$)
  const smokingAllowed = get(smokingAllowed$)
  const seatAttributes = get(seatAttributes$)
  const childUses = get(childUses$)
  return showFilterChips({
    buffet,
    freeFlows,
    afternoonTea,
    smokingAllowed,
    seatAttributes,
    childUses,
  })
})
show$.debugLabel = `${DEBUG_PREFIX}-show$`

export function useShow() {
  return useAtomValue(show$)
}

const courses$ = atom<ResultOf<typeof RestaurantInventoryCourse>[]>((get) => {
  return (
    get(coursesQuery$).data?.restaurant?.inventory.courses.map((c) =>
      getFragmentData(RestaurantInventoryCourse, c)
    ) ?? []
  )
})
courses$.debugLabel = `${DEBUG_PREFIX}-courses$`

const filteredCourses$ = atom<ResultOf<typeof RestaurantInventoryCourse>[]>((get) => {
  const courses = get(courses$)

  const criteria = get(filterCriteria$)
  // 絞り込み条件が指定されていない場合は、バックエンドから取得したコースをそのまま表示する
  if (criteria === undefined) {
    return courses
  }

  return (
    courses
      .map((c) => {
        const plans = c.plans.filter(
          (p) =>
            filterPlanByCriteria(getFragmentData(PlanAttributes, p.plan), criteria) &&
            filterPlanByPriceRange(p.price, criteria) &&
            p.seats.some(
              (s) =>
                filterSeatByCriteria(getFragmentData(SeatAttributesFragment, s), criteria) &&
                filterSeatBySeatType(getFragmentData(SeatAttributesFragment, s), criteria)
            )
        )
        return { ...c, plans }
      })
      .filter((c) => c.plans.length > 0) ?? []
  )
})
filteredCourses$.debugLabel = `${DEBUG_PREFIX}-courses$`

const allPrices$ = atom<number[]>((get) => {
  return (
    get(coursesQuery$)
      .data?.restaurant?.inventory.courses.flatMap(
        (c) => getFragmentData(RestaurantInventoryCourse, c).plans
      )
      .map((p) => p.price) ?? []
  )
})
allPrices$.debugLabel = `${DEBUG_PREFIX}-allPrices$`

const initialPriceRange$ = atom<PriceCriteria>((get) => {
  return {
    lowerBoundPrice: Math.min(...get(allPrices$)),
    upperBoundPrice: Math.max(...get(allPrices$)),
  }
})
initialPriceRange$.debugLabel = `${DEBUG_PREFIX}-initialPriceRange$`

export function useInitialPriceRange() {
  return useAtomValue(initialPriceRange$)
}

export function useCourses() {
  return useAtomValue(filteredCourses$)
}

const seatOnlyPlans$ = atom(
  (get) =>
    get(coursesQuery$).data?.restaurant?.inventory.seatOnlyPlans.map((p) => {
      return { ...getFragmentData(PlanSelectionInfo, p.plan), fixedPoints: p.fixedPoints }
    }) ?? []
)
seatOnlyPlans$.debugLabel = `${DEBUG_PREFIX}-seatOnlyPlans$`

const filteredSeatOnlyPlans$ = atom((get) => {
  const criteria = get(filterCriteria$)
  if (criteria === undefined) {
    // 絞り込み条件が指定されていない場合は、バックエンドから取得したコースをそのまま表示する
    return get(seatOnlyPlans$)
  }

  return (
    get(coursesQuery$)
      .data?.restaurant?.inventory.seatOnlyPlans.filter(
        // FIXME: この部分は共通化できそう
        (e) =>
          filterPlanByCriteria(getFragmentData(PlanAttributes, e.plan), criteria) &&
          e.seats.some(
            (s) =>
              filterSeatByCriteria(getFragmentData(SeatAttributesFragment, s), criteria) &&
              filterSeatBySeatType(getFragmentData(SeatAttributesFragment, s), criteria)
          )
      )
      .map((p) => {
        return { ...getFragmentData(PlanSelectionInfo, p.plan), fixedPoints: p.fixedPoints }
      }) ?? []
  )
})
filteredSeatOnlyPlans$.debugLabel = `${DEBUG_PREFIX}-filteredSeatOnlyPlans$`

export function useSeatOnlyPlans() {
  return useAtomValue(filteredSeatOnlyPlans$)
}

const restaurant$ = atom((get) => get(coursesQuery$).data?.restaurant)
restaurant$.debugLabel = `${DEBUG_PREFIX}-restaurant$`

export function useRestaurant() {
  return useAtomValue(restaurant$)
}

const filteredAllPlans$ = atom((get) => {
  return get(filteredCourses$).flatMap((c) =>
    c.plans.flatMap((p) => getFragmentData(PlanAttributes, p.plan))
  )
})
filteredAllPlans$.debugLabel = `${DEBUG_PREFIX}-filteredAllPlans$`

const filteredAllSeats$ = atom((get) => {
  return get(filteredCourses$)
    .flatMap((c) => c.plans.flatMap((p) => p.seats))
    .map((s) => getFragmentData(SeatAttributesFragment, s))
})
filteredAllSeats$.debugLabel = `${DEBUG_PREFIX}-filteredAllSeats$`

const enabledFreeFlows$ = atom((get) => {
  return hasFreeFlow(get(filteredAllPlans$), get(filterCriteria$))
})

export function useEnabledFreeFlows() {
  return useAtomValue(enabledFreeFlows$)
}

const enabledAfternoonTea$ = atom((get) => {
  return hasAfternoonTea(get(filteredAllPlans$), get(filterCriteria$))
})

export function useEnabledAfternoonTea() {
  return useAtomValue(enabledAfternoonTea$)
}

const enabledChristmas$ = atom((get) => {
  return hasChristmas(get(filteredAllPlans$), get(filterCriteria$))
})

export function useEnabledChristmas() {
  return useAtomValue(enabledChristmas$)
}

const enabledBuffet$ = atom((get) => {
  return hasBuffet(get(filteredAllPlans$), get(filterCriteria$))
})

export function useEnabledBuffet() {
  return useAtomValue(enabledBuffet$)
}

const enabledSmokingAllowed$ = atom((get) => {
  return hasSmokingAllowed(get(filteredAllSeats$), get(filterCriteria$))
})

export function useEnabledSmokingAllowed() {
  return useAtomValue(enabledSmokingAllowed$)
}

const enabledSeatAttributes$ = atom((get) => {
  return hasSeatAttributes(get(filteredAllSeats$), get(filterCriteria$))
})

export function useEnabledSeatAttributes() {
  return useAtomValue(enabledSeatAttributes$)
}

const enabledChildUses$ = atom((get) => {
  return hasChildUses(get(filteredAllSeats$), get(filterCriteria$))
})

export function useEnabledChildUse() {
  return useAtomValue(enabledChildUses$)
}

export function useSetFreeFlows() {
  return useBaseSetFreeFlows('COURSE_SELECT')
}

export function useSetAfternoonTea() {
  return useBaseSetAfternoonTea('COURSE_SELECT')
}

export function useSetChristmas() {
  return useBaseSetChristmas('COURSE_SELECT')
}
